/**
 * The phase band's writes: adding a phase, copying one, and editing or deleting the one in view.
 *
 * ── Selecting what was just created is part of the act ───────────────────────────────────────
 * A phase appended at the tail of a twelve-phase band is off the right edge of the strip, and a copy
 * lands beside a source the trainer may not be standing on. Both operations answer with the index
 * they wrote to, so this hook moves `?phase=` there — which scrolls the tab into view, re-renders
 * the canvas on the new block and puts the settings popover on it. Without that, "duplicate this
 * phase" looked like it had done nothing.
 *
 * ── One pending act, one prompt ──────────────────────────────────────────────────────────────
 * Same shape as `useDayActions`: there is exactly one phase act waiting on an answer at a time, so
 * there is one prompt. Its words come from the view model, which is where the fork-versus-template
 * wording and the shift sentence are decided; this hook only holds what is waiting and what happens
 * when it is answered.
 *
 * The act is captured as a CLOSURE at request time, together with its prompt. Between the question
 * and the answer the trainer can undo, add a phase or switch tabs, and re-reading the tree at
 * confirm time would act on whatever now sits at that index.
 *
 * ── Two acts raise it, and for two different reasons ─────────────────────────────────────────
 * A DELETE always asks: it destroys a block. A DUPLICATE asks only when the copy would move a stored
 * phase of a running fork — `toPhaseShiftPrompt` answers `null` everywhere else, so a library
 * template and an append at the tail stay one press. That asymmetry is the point: a confirmation in
 * front of a free, undoable edit is one the trainer learns to dismiss without reading.
 *
 * **Resizing deliberately does NOT raise it.** The stepper fires once per press and a trainer holds
 * it; a dialog per cycle would be unusable. The save-time consent
 * (`structuralEditFlow.structuralSaveRoute`) covers a resize that moves a later phase, with the
 * server's own count where the server can produce one. Recorded here rather than left to be
 * rediscovered.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  addPhase,
  duplicatePhase,
  readPlanDraft,
  removePhase,
  setPhaseCycles,
  setPhaseName,
  setPhaseType,
  type PhaseType,
} from '~/entities/plan-draft';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import { KEY } from '../model/builderCopy';
import {
  toPhaseShiftPrompt,
  type PhaseActionPrompt,
  type PhaseEditorView,
} from '../model/phaseEditorView';
import type { PhaseAddActions } from '../ui/PhaseAddMenu';
import type { PhaseEditorActions } from '../ui/PhaseEditor';

/** The act a confirm is open over, and the words it was opened with. */
interface PendingPhaseAct {
  readonly prompt: PhaseActionPrompt;
  /** What the confirm commits. Captured with the prompt — see the header. */
  readonly run: () => void;
  /** Whether the band is owed focus afterwards. True for a deletion, whose ⋯ changes meaning. */
  readonly focusBand: boolean;
}

export interface PhaseEditing {
  readonly add: PhaseAddActions;
  /** Bound to the phase in view — the only phase the band's editor can act on. */
  readonly edit: PhaseEditorActions;
  readonly prompt: ConfirmPrompt | null;
  readonly confirmAct: () => void;
  readonly cancelAct: () => void;
  /**
   * Bumped when a deletion is CONFIRMED, so the band can take focus back.
   *
   * A cancelled confirm needs nothing — the editor's popup restores focus to the ⋯ it was opened
   * from, and the dialog hands it back there. A confirmed one deletes the phase that ⋯ was editing
   * and moves the canvas to its neighbour, so the band is what changed and the band is where a
   * keyboard trainer has to land. Same shape as the dock's `focusSignal`: a counter, because the
   * same act can happen twice in a row and the second one still has to move focus.
   */
  readonly bandFocusSignal: number;
  /**
   * What the last phase act did, in words, for the screen's live region. Empty until one happens.
   *
   * The band's own total is a live region and it already changes — but "13 tygodni · 4 fazy" does
   * not say WHICH phase appeared, and a copy lands on a tab the trainer was not standing on. Same
   * shape as `useDayActions`' announcement, one level up.
   */
  readonly announcement: string;
}

/**
 * @param phaseIndex The phase the canvas is showing — the one the editor acts on.
 * @param view Its worded settings panel, or `null` when the plan cannot be edited. Both prompts the
 *   band raises are read off it.
 * @param selectPhase Move `?phase=` — see the header on why creating a phase selects it.
 */
export function usePhaseEditing(
  phaseIndex: number,
  view: PhaseEditorView | null,
  selectPhase: (index: number) => void,
): PhaseEditing {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingPhaseAct | null>(null);
  const [bandFocusSignal, setBandFocusSignal] = useState(0);
  const [announcement, setAnnouncement] = useState('');

  /**
   * Select what was created and say what it was — but only when the operation RAN. A refusal
   * answers `null`, and announcing a phase that was never made is worse than saying nothing.
   */
  const created = (index: number | null, message: string) => {
    if (index === null) return;
    selectPhase(index);
    setAnnouncement(message);
  };

  /**
   * Copy a phase — asking first when the copy would move a block the client is already scheduled on.
   *
   * The prompt is resolved from the CURRENT tree at the press rather than pre-built per render:
   * twelve phases would mean twelve prompts nobody reads, and the tree the question describes must
   * be the one the press acts on.
   */
  const duplicate = (index: number, name: string): 'asked' | 'done' => {
    const run = () => {
      created(duplicatePhase(index, name), t(`${KEY}.phase.duplicated`, { name }));
    };
    const draft = readPlanDraft();
    const prompt =
      draft === null ? null : toPhaseShiftPrompt(t, draft, { kind: 'duplicate', phaseIndex: index });
    if (prompt === null) {
      run();
      return 'done';
    }
    setPending({ prompt, run, focusBand: false });
    return 'asked';
  };

  return {
    add: {
      add: (type: PhaseType, name: string) => {
        created(addPhase(type, name), t(`${KEY}.phase.added`, { name }));
      },
      duplicate,
    },
    edit: {
      rename: (name: string) => {
        setPhaseName(phaseIndex, name);
      },
      retype: (type: PhaseType) => {
        setPhaseType(phaseIndex, type);
      },
      resize: (cycles: number) => {
        setPhaseCycles(phaseIndex, cycles);
      },
      duplicate: (name: string) => duplicate(phaseIndex, name),
      requestDelete: () => {
        if (view === null) return;
        const deletePrompt = view.deletePrompt;
        const draft = readPlanDraft();
        // The deletion's own consequence, and then the blocks it moves. One dialog: the trainer is
        // answering one question, and a second prompt after the first is how a confirm stops being
        // read.
        const shift =
          draft === null
            ? null
            : toPhaseShiftPrompt(t, draft, { kind: 'remove', phaseIndex });
        setPending({
          prompt:
            shift === null
              ? deletePrompt
              : {
                  ...deletePrompt,
                  description: `${deletePrompt.description} ${shift.description}`,
                },
          run: () => {
            removePhase(phaseIndex);
            // The deleted phase's index now names its successor, or nothing at all when the last
            // phase went. `resolvePhaseIndex` clamps a `?phase=` past the end, so stepping back one
            // keeps the address on a phase that exists rather than on one the clamp has to rescue.
            selectPhase(Math.max(0, phaseIndex - 1));
            // The band is where focus goes and the tab that named the phase is gone with it, so the
            // only thing left to say what happened is this.
            setAnnouncement(t(`${KEY}.phase.deleted`, { name: deletePrompt.name }));
          },
          focusBand: true,
        });
      },
    },
    prompt:
      pending === null
        ? null
        : {
            title: pending.prompt.title,
            description: pending.prompt.description,
            confirmLabel: pending.prompt.confirmLabel,
            cancelLabel: pending.prompt.cancelLabel,
            // A deletion destroys the phase and every day under it, and no answer here keeps it — so
            // its confirm is drawn in `--danger` and Cancel stays the neutral ghost. A COPY is
            // constructive, so the ember stays on its confirm where it belongs.
            destructiveConfirm: pending.focusBand,
          },
    confirmAct: () => {
      if (pending === null) return;
      pending.run();
      setPending(null);
      if (pending.focusBand) setBandFocusSignal((current) => current + 1);
    },
    cancelAct: () => {
      setPending(null);
    },
    bandFocusSignal,
    announcement,
  };
}
